import isEqualWith from 'lodash/isEqualWith';
import set from 'lodash/set';
import has from 'lodash/has';
import expect from '@src/slide/infrastructure/module/independent/assert/expect';

// 比较的key值
const COMPARE_KEY_COLOR = 'color';
// 数值误差区间
const NUMBER_REGION_DIFF = [
    { min: 0, max: 1, diff: 0.01 },
    { min: 1, max: 20, diff: 0.1 },
    { min: 20, max: 80, diff: 0.4 },
    { min: 80, max: 200, diff: 1 },
    { min: 200, max: Number.MAX_VALUE, diff: 2 },
];

const FILTER_KEYS = [
    'lang',
];
/**
 * 空字段信息
 */
interface EmptyInfo {
    obj: any;
    key: string;
    value: any;
}

/**
 * 比较两个slate editor数据是否一致
 * 注意：比较后会移除值为空对象、null和undefined的字段
 */
export class EditorDataCompareEngine {
    private static instance;
    public static getInstance() {
        if (!EditorDataCompareEngine.instance) {
            EditorDataCompareEngine.instance = new EditorDataCompareEngine();
        }
        return EditorDataCompareEngine.instance;
    }

    private lastKey: string;
    private restoreEmptyCache: EmptyInfo[] = [];

    public compare(objA, objB): boolean {
        const rV =  isEqualWith(objA, objB, this.compareCustomizer.bind(this));
        this.restoreEmptyOrUndefined();
        return rV;
    }

    private compareCustomizer(objValue, othValue, key: string) {
        // 缓存最后一次比较的值
        this.lastKey = key;

        // 1. 移除空字段, 比较完毕之后会进行还原
        this.filterEmptyOrUndefined(objValue);
        this.filterEmptyOrUndefined(othValue);

        // 2. 浮点数值比较
        if (this.compareNumber(objValue, othValue)) {
            return true;
        }

        // 3. color比较
        if (this.compareSolidColor(objValue, othValue, key)) {
            return true;
        }
    }

    /**
     * 过滤对象中的空字段
     * @param checkObj
     */
    private filterEmptyOrUndefined(checkObj) {
        const object = checkObj;
        if (expect(object).isObj()) {
            Object.keys(object).forEach((key) => {
                if (expect(object[key]).isNullOrUndefined() || expect(object[key]).isEmptyObj()) {
                    this.restoreEmptyCache.push({ obj: object, key, value: object[key] });
                    delete object[key];
                } else {
                    const pObj = object[key];
                    if (expect(pObj).isObj()) {
                        FILTER_KEYS.forEach((filterKey) => { // 删除过滤的字段
                            if (has(pObj, filterKey)) {
                                this.restoreEmptyCache.push({ obj: pObj, key: filterKey, value: pObj[filterKey] });
                                delete pObj[filterKey];
                            }
                        });
                        if (expect(pObj).isEmptyObj()) {  // 删除对象字段后，对象为空，则还要删除这个对象
                            this.restoreEmptyCache.push({ obj: object, key, value: pObj });
                            delete object[key];
                        }
                    }
                }
            });
        }
    }

    /**
     * 还原对象中的空字段
     */
    private restoreEmptyOrUndefined() {
        this.restoreEmptyCache.forEach((emptyInfo: EmptyInfo) => {
            set(emptyInfo.obj, emptyInfo.key, emptyInfo.value);
        });
        this.restoreEmptyCache = [];  // 重置
    }

    /**
     * 自定义solidColor比较
     * @param objValue
     * @param othValue
     * @param key
     */
    private compareSolidColor(objValue, othValue, key: string): boolean {
        if (key === COMPARE_KEY_COLOR && expect(objValue).isObj() && expect(othValue).isObj()) {
            return objValue.srgb_color === othValue.srgb_color;
        }
        return false;
    }

    /**
     * 对比浮点数，允许在不同区间，数值误差不同
     * @param objValue
     * @param othValue
     */
    private compareNumber(objValue, othValue): boolean {
        if (expect(objValue).isNumber() && expect(othValue).isNumber()) {
            let diff = 0;
            NUMBER_REGION_DIFF.forEach((item) => {
                if (Math.abs(objValue) >= item.min && Math.abs(objValue) < item.max) {
                    diff = item.diff;
                }
            });
            return Math.abs(objValue - othValue) <= diff;
        }
        return false;
    }
}

const editorCompareEngine = EditorDataCompareEngine.getInstance();
export default editorCompareEngine;
